import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import { formatDate, formatPercent } from './TrilhaStudentsShared';

const BLOCK_STATE = {
  done: { label: 'Concluído', variant: 'success' },
  skipped: { label: 'Pulado', variant: 'warning' },
  pending: { label: 'Pendente', variant: 'neutral-lightest' }
};

const BLOCK_KIND = {
  content: 'Conteúdo',
  review: 'Revisão'
};

// Drill-down de uma trilha do aluno: blocos com estado. `student.id` é o id do UserLearningPath da linha.
export default function TrilhaProgressModal({ open, onOpenChange, student }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const userLearningPathId = student?.id;

  useEffect(() => {
    if (!open || !userLearningPathId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    adminLearningPathsApi
      .studentDetail(userLearningPathId)
      .then(json => setDetail(json?.data?.attributes || null))
      .catch(() => toast.error('Erro ao carregar progresso do aluno'))
      .finally(() => setLoading(false));
  }, [open, userLearningPathId]);

  const blocks = detail?.blocks || [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        size="lg"
        className="overflow-x-hidden"
      >
        <DialogHeader>
          <DialogTitle>{student?.user_name}</DialogTitle>
          <DialogDescription>
            Progresso na trilha {student?.title || detail?.learning_path_name || ''} — {formatPercent(student?.percent_walked)} concluído, {formatPercent(student?.percent_mastered)} dominado.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0">
          {loading ? (
            <p className="py-4 text-center text-sm text-neutral">Carregando blocos...</p>
          ) : (
            <ul className="space-y-2">
              {blocks.map(b => {
                const state = BLOCK_STATE[b.state] || BLOCK_STATE.pending;
                return (
                  <li
                    key={b.position}
                    className="flex items-start gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
                  >
                    <span className="w-8 shrink-0 text-center font-mono text-xs text-neutral">{b.position + 1}</span>
                    <div className="min-w-0 flex-1 basis-40">
                      <div className="flex items-center justify-between gap-2">
                        <p className="mb-0 min-w-0 truncate text-sm font-medium text-primary-dark">{b.video_title || b.taxonomy_name || BLOCK_KIND[b.kind]}</p>
                        <Badge
                          variant={state.variant}
                          className="shrink-0"
                        >
                          {state.label}
                        </Badge>
                      </div>
                      <p className="mb-0 text-xs text-neutral">
                        {BLOCK_KIND[b.kind] || b.kind}
                        {b.completed_at ? ` · concluído em ${formatDate(b.completed_at)}` : ''}
                      </p>
                      {b.taxonomy_name && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          <Badge
                            variant="muted"
                            size="xs"
                            className="font-bold text-neutral-dark"
                          >
                            {b.taxonomy_name}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
              {blocks.length === 0 && <p className="py-4 text-center text-sm text-neutral">Sem blocos para exibir.</p>}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
